(() => {
  if (customElements.get('atoto-navigation')) return;
  class AtotoNavigation extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.trigger = this.querySelector('[data-nav-trigger]');
      this.dialog = this.querySelector('dialog');
      this.shade = this.querySelector('[data-nav-shade]') || document.createElement('div');
      this.shade.className = 'atoto-nav-shade';
      this.shade.hidden = true;
      this.back = this.dialog.querySelector('[data-nav-back]');
      this.closeButton = this.dialog.querySelector('[data-nav-close]');
      this.titleNode = this.dialog.querySelector('[data-nav-title]');
      this.scroller = this.dialog.querySelector('.atoto-nav-scroll');
      this.buttons = [...this.dialog.querySelectorAll('[data-nav-category]')];
      this.panels = [...this.dialog.querySelectorAll('[data-nav-panel]')];
      this.main = this.closest('main-menu');
      this.mobile = matchMedia('(max-width: 768px)');
      this.abort = new AbortController();
      const on = (node, event, fn) => node?.addEventListener(event, fn, { signal: this.abort.signal });
      // Fixed overlays must not inherit a transformed header's containing block.
      document.body.append(this.shade, this.dialog);
      on(this.trigger, 'click', event => {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.dialog.open ? this.close() : this.open();
      });
      on(this.trigger, 'keydown', event => {
        if (event.key === 'ArrowDown' || event.key === ' ') {
          event.preventDefault(); this.open(); this.buttons[0]?.focus();
        }
      });
      on(this.closeButton, 'click', () => this.close());
      on(this.shade, 'click', () => this.close());
      on(this.back, 'click', () => this.categories(true));
      on(this.dialog, 'cancel', event => { event.preventDefault(); this.close(); });
      on(document, 'keydown', event => {
        if (event.key === 'Escape' && this.dialog.open) { event.preventDefault(); this.close(); }
      });
      on(document, 'pointerdown', event => {
        if (this.dialog.open && !this.dialog.contains(event.target) && !this.contains(event.target)) this.close(false);
      });
      on(document, 'focusin', event => {
        if (this.dialog.open && !this.mobile.matches && !this.dialog.contains(event.target) && !this.contains(event.target)) this.close(false);
      });
      on(this.dialog, 'click', event => {
        if (event.target === this.dialog) {
          const rect = this.dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
        }
        if (event.target.closest('a[href]')) this.saveReturn();
      });
      // Keep the selected panel stable while the pointer travels to its links.
      this.buttons.forEach(button => {
        button.hidden = !this.panels.some(panel => panel.dataset.navPanel === button.dataset.navCategory);
        if (!button.hasAttribute('data-nav-direct')) {
          on(button, 'click', () => this.select(button.dataset.navCategory, true));
        }
        on(button, 'keydown', event => {
          const visible = this.buttons.filter(item => !item.hidden);
          const current = visible.indexOf(button);
          let next;
          if (event.key === 'ArrowDown') next = (current + 1) % visible.length;
          if (event.key === 'ArrowUp') next = (current - 1 + visible.length) % visible.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = visible.length - 1;
          if (next !== undefined) {
            event.preventDefault(); visible[next].focus();
            if (!this.mobile.matches) this.select(visible[next].dataset.navCategory);
          }
        });
      });
      on(window, 'resize', () => { if (this.dialog.open) this.position(); });
      on(window, 'scroll', () => { if (this.dialog.open && !this.mobile.matches) this.position(); });
      on(this.mobile, 'change', () => { if (this.dialog.open) this.close(); });
      on(window, 'pagehide', () => this.close(false));
      on(document, 'shopify:block:select', event => {
        if (this.contains(event.target) || this.dialog.contains(event.target)) {
          this.open(); const panel = event.target.closest('[data-nav-panel]');
          if (panel) this.select(panel.dataset.navPanel, true);
        }
      });
      if (this.dataset.menuKey === 'stereo' && new URLSearchParams(location.search).get('stereo_menu') === 'open') this.open();
    }
    disconnectedCallback() {
      this.close(false); this.abort?.abort();
      if (this.dialog) this.append(this.dialog);
      if (this.shade) this.append(this.shade);
      this.ready = false;
    }
    position() {
      const top = Math.max(0, this.closest('store-header')?.getBoundingClientRect().bottom || 0);
      this.dialog.style.setProperty('--atoto-nav-top', `${top}px`); this.shade.style.top = `${top}px`;
    }
    open() {
      if (this.dialog.open) return;
      document.querySelectorAll('atoto-navigation').forEach(menu => { if (menu !== this) menu.close(false); });
      this.main?.nav?.querySelectorAll('details.is-open').forEach(details => this.main.close(details));
      this.main?.toggleOverlay(false);
      if (typeof removeTrapFocus === 'function') removeTrapFocus();
      const firstCategory = this.buttons.find(button => !button.hidden && !button.hasAttribute('data-nav-direct'));
      this.dialog.toggleAttribute('data-no-panels', !firstCategory);
      if (firstCategory) this.select(firstCategory.dataset.navCategory);
      this.categories(); this.position();
      this.trigger.setAttribute('aria-expanded', 'true');
      if (this.mobile.matches) this.dialog.showModal();
      else { this.dialog.show(); this.shade.hidden = false; }
      document.documentElement.classList.add('atoto-navigation-open');
      this.closeButton.focus({ preventScroll: true });
    }
    close(focus = true) {
      if (!this.dialog?.open) return;
      this.dialog.close(); this.shade.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
      document.documentElement.classList.remove('atoto-navigation-open');
      if (focus) {
        if (this.mobile.matches && !this.main?.mainDisclosure?.classList.contains('is-open')) this.main?.mainToggle?.focus();
        else this.trigger.focus({ preventScroll: true });
      }
    }
    select(key, enter = false) {
      const category = this.buttons.find(item => item.dataset.navCategory === key);
      if (!category || category.hasAttribute('data-nav-direct')) return;
      const panel = this.panels.find(item => item.dataset.navPanel === key);
      if (!panel) return;
      const template = panel.querySelector('template');
      if (template) { panel.append(template.content.cloneNode(true)); template.remove(); }
      this.buttons.filter(button => !button.hasAttribute('data-nav-direct')).forEach(button => button.setAttribute('aria-pressed', String(button.dataset.navCategory === key)));
      this.panels.forEach(item => { item.hidden = item !== panel; }); this.active = key;
      if (this.mobile.matches && enter) {
        this.categoryScroll = this.scroller.scrollTop; this.dialog.dataset.level = 'content'; this.back.hidden = false;
        this.titleNode.textContent = this.buttons.find(button => button.dataset.navCategory === key).textContent.replace('›', '').trim();
        this.scroller.scrollTop = 0; this.back.focus({ preventScroll: true });
      }
    }
    categories(focus = false) {
      this.dialog.dataset.level = 'categories'; this.back.hidden = true;
      this.titleNode.textContent = this.dataset.menuName; this.scroller.scrollTop = this.categoryScroll || 0;
      if (focus) this.buttons.find(button => button.dataset.navCategory === this.active)?.focus({ preventScroll: true });
    }
    saveReturn() {
      const search = document.querySelector('atoto-sbv-live');
      if (!search?.inputs || !['year', 'make', 'model'].every(key => search.valid(key))) return;
      search.persistSelection();
      const url = new URL(location.href); url.searchParams.set('sbv_restore', JSON.stringify(search.searchSnapshot()));
      history.replaceState(history.state, '', url);
    }
  }
  customElements.define('atoto-navigation', AtotoNavigation);
})();
